import { useState, useEffect, useRef, useCallback } from "react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { GameLayout } from "@/components/game/GameLayout";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gamepad2, Heart, Trophy, Lock } from "lucide-react";

interface Enemy {
  x: number;
  y: number;
  alive: boolean;
  type: number;
  hit: number;
  hp: number;
}

interface Bullet { x: number; y: number; }
interface EnemyBullet { x: number; y: number; speed: number; }
interface Particle {
  x: number; y: number; vx: number; vy: number;
  life: number; color: string; size: number;
}
interface PowerUp {
  x: number; y: number; type: "shield" | "rapid" | "multi";
  life: number;
}

const CANVAS_W = 360;
const CANVAS_H = 460;
const PLAYER_W = 36;
const PLAYER_H = 36;
const ENEMY_W = 28;
const ENEMY_H = 24;
const BULLET_W = 3;
const BULLET_H = 14;

export default function GameMiniGame() {
  const { session, player, loading, signOut, refreshPlayer } = useGameAuth();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameState, setGameState] = useState<"menu" | "playing" | "gameover">("menu");
  const [score, setScore] = useState(0);
  const [wave, setWave] = useState(1);
  const [livesLeft, setLivesLeft] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reward, setReward] = useState<number | null>(null);
  const [loadingLives, setLoadingLives] = useState(true);
  const [unlocked, setUnlocked] = useState<boolean | null>(null);

  const gameRef = useRef({
    playerX: CANVAS_W / 2 - PLAYER_W / 2,
    playerLives: 3,
    bullets: [] as Bullet[],
    enemyBullets: [] as EnemyBullet[],
    enemies: [] as Enemy[],
    particles: [] as Particle[],
    powerUps: [] as PowerUp[],
    enemyDir: 1,
    enemySpeed: 0.3,
    score: 0,
    frame: 0,
    gameOver: false,
    animId: 0,
    lastShot: 0,
    wave: 1,
    stars: [] as { x: number; y: number; speed: number; brightness: number }[],
    playerGlow: 0,
    shieldTimer: 0,
    rapidFireTimer: 0,
    multiShotTimer: 0,
    invincibleTimer: 0,
    screenShake: 0,
    comboCount: 0,
    comboTimer: 0,
    // Touch state
    touchActive: false,
    touchX: CANVAS_W / 2,
    autoFire: true,
  });

  useEffect(() => {
    if (player) {
      fetchLives();
      checkUnlock();
    }
  }, [player]);

  const checkUnlock = async () => {
    if (!player) return;
    const { data } = await supabase
      .from("game_space_invaders_unlocks")
      .select("id")
      .eq("player_id", player.id)
      .maybeSingle();
    setUnlocked(!!data);
  };

  useEffect(() => {
    const stars = [];
    for (let i = 0; i < 80; i++) {
      stars.push({
        x: Math.random() * CANVAS_W,
        y: Math.random() * CANVAS_H,
        speed: 0.15 + Math.random() * 0.5,
        brightness: 0.15 + Math.random() * 0.7,
      });
    }
    gameRef.current.stars = stars;
  }, []);

  const fetchLives = async () => {
    if (!player) return;
    setLoadingLives(true);
    const { data } = await supabase
      .from("game_daily_lives")
      .select("lives_used")
      .eq("player_id", player.id)
      .eq("play_date", new Date().toISOString().split("T")[0])
      .maybeSingle();
    setLivesLeft(2 - (data?.lives_used || 0));
    setLoadingLives(false);
  };

  const spawnEnemies = useCallback((w: number) => {
    const rows = Math.min(3 + Math.floor(w / 2), 5);
    const cols = Math.min(6 + Math.floor(w / 3), 8);
    const enemies: Enemy[] = [];
    const offsetX = (CANVAS_W - cols * (ENEMY_W + 10)) / 2;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        enemies.push({
          x: offsetX + c * (ENEMY_W + 10),
          y: 36 + r * (ENEMY_H + 12),
          alive: true,
          type: r % 3,
          hit: 0,
          hp: w >= 4 && r === 0 ? 2 : 1,
        });
      }
    }
    return enemies;
  }, []);

  const spawnExplosion = (x: number, y: number, color: string, count = 10) => {
    const g = gameRef.current;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const speed = 1.5 + Math.random() * 2.5;
      g.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 20 + Math.random() * 15,
        color,
        size: 2 + Math.random() * 4,
      });
    }
  };

  const startGame = () => {
    if (unlocked === false) {
      toast.error("Space Invaders is locked. Unlock from the Dashboard for $1.");
      navigate("/game");
      return;
    }
    if (livesLeft !== null && livesLeft <= 0) {
      toast.error("No lives left today! Come back tomorrow 🕐");
      return;
    }
    const g = gameRef.current;
    g.playerX = CANVAS_W / 2 - PLAYER_W / 2;
    g.playerLives = 3;
    g.bullets = [];
    g.enemyBullets = [];
    g.particles = [];
    g.powerUps = [];
    g.enemies = spawnEnemies(1);
    g.enemyDir = 1;
    g.enemySpeed = 0.3;
    g.score = 0;
    g.frame = 0;
    g.gameOver = false;
    g.lastShot = 0;
    g.wave = 1;
    g.playerGlow = 0;
    g.shieldTimer = 0;
    g.rapidFireTimer = 0;
    g.multiShotTimer = 0;
    g.invincibleTimer = 0;
    g.screenShake = 0;
    g.comboCount = 0;
    g.comboTimer = 0;
    g.touchActive = false;
    g.autoFire = true;
    setScore(0);
    setWave(1);
    setReward(null);
    setGameState("playing");
  };

  const submitScore = async (finalScore: number) => {
    if (!player || submitting) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc("submit_flow_invaders_score", {
        p_player_id: player.id,
        p_score: finalScore,
      });
      if (error) throw error;
      const result = data?.[0] || data;
      setReward(Number(result?.reward_amount || 0));
      setLivesLeft(Number(result?.lives_left ?? 0));
      await refreshPlayer();
      toast.success(`+$${Number(result?.reward_amount || 0).toFixed(2)} earned! 🎮`);
    } catch (err: any) {
      toast.error(err.message || "Failed to submit score");
    } finally {
      setSubmitting(false);
    }
  };

  // Game loop
  useEffect(() => {
    if (gameState !== "playing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const g = gameRef.current;
    let ended = false;

    // Keyboard controls - smooth movement
    const keysDown = new Set<string>();
    const handleKeyDown = (e: KeyboardEvent) => {
      keysDown.add(e.key);
      if (e.key === " " || e.key === "ArrowUp") e.preventDefault();
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      keysDown.delete(e.key);
    };

    // Touch controls - smooth dragging
    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      g.touchActive = true;
      const rect = canvas.getBoundingClientRect();
      const scale = CANVAS_W / rect.width;
      g.touchX = (e.touches[0].clientX - rect.left) * scale;
    };
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (!g.touchActive) return;
      const rect = canvas.getBoundingClientRect();
      const scale = CANVAS_W / rect.width;
      g.touchX = (e.touches[0].clientX - rect.left) * scale;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      g.touchActive = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
    canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: false });

    const drawPlayer = (px: number, py: number) => {
      g.playerGlow = (g.playerGlow + 0.06) % (Math.PI * 2);
      const glowIntensity = 0.3 + Math.sin(g.playerGlow) * 0.15;

      // Engine glow - dual thrusters
      for (const offset of [-6, 6]) {
        const engineGrad = ctx.createRadialGradient(
          px + PLAYER_W / 2 + offset, py + PLAYER_H + 6, 0,
          px + PLAYER_W / 2 + offset, py + PLAYER_H + 6, 14
        );
        engineGrad.addColorStop(0, `rgba(0, 220, 255, ${glowIntensity + 0.4})`);
        engineGrad.addColorStop(0.4, `rgba(0, 150, 255, ${glowIntensity})`);
        engineGrad.addColorStop(1, "rgba(0, 100, 255, 0)");
        ctx.fillStyle = engineGrad;
        ctx.fillRect(px + PLAYER_W / 2 + offset - 6, py + PLAYER_H - 2, 12, 18);
      }

      // Shield visual
      if (g.shieldTimer > 0 || g.invincibleTimer > 0) {
        ctx.strokeStyle = `rgba(34, 211, 238, ${0.4 + Math.sin(g.frame * 0.15) * 0.2})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px + PLAYER_W / 2, py + PLAYER_H / 2, PLAYER_W / 2 + 6, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = `rgba(34, 211, 238, 0.08)`;
        ctx.fill();
      }

      // Ship body - sleeker design
      ctx.fillStyle = "#10b981";
      ctx.beginPath();
      ctx.moveTo(px + PLAYER_W / 2, py - 2);
      ctx.lineTo(px + 2, py + PLAYER_H - 4);
      ctx.lineTo(px + PLAYER_W / 2, py + PLAYER_H - 10);
      ctx.lineTo(px + PLAYER_W - 2, py + PLAYER_H - 4);
      ctx.closePath();
      ctx.fill();

      // Wings
      ctx.fillStyle = "#059669";
      ctx.beginPath();
      ctx.moveTo(px + 2, py + PLAYER_H - 4);
      ctx.lineTo(px - 6, py + PLAYER_H + 4);
      ctx.lineTo(px + 10, py + PLAYER_H - 10);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(px + PLAYER_W - 2, py + PLAYER_H - 4);
      ctx.lineTo(px + PLAYER_W + 6, py + PLAYER_H + 4);
      ctx.lineTo(px + PLAYER_W - 10, py + PLAYER_H - 10);
      ctx.closePath();
      ctx.fill();

      // Cockpit glow
      const cockpitGrad = ctx.createRadialGradient(
        px + PLAYER_W / 2, py + 12, 0,
        px + PLAYER_W / 2, py + 12, 5
      );
      cockpitGrad.addColorStop(0, "#a5f3fc");
      cockpitGrad.addColorStop(1, "#22d3ee");
      ctx.fillStyle = cockpitGrad;
      ctx.beginPath();
      ctx.arc(px + PLAYER_W / 2, py + 12, 4, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawEnemy = (e: Enemy) => {
      if (!e.alive) return;
      const colors = [
        ["#ef4444", "#dc2626", "#fca5a5"],
        ["#f59e0b", "#d97706", "#fde68a"],
        ["#a855f7", "#9333ea", "#d8b4fe"],
      ];
      const [main, dark, light] = colors[e.type];
      const flash = e.hit > 0;

      // Glow under enemy
      if (!flash) {
        ctx.fillStyle = `${main}15`;
        ctx.beginPath();
        ctx.ellipse(e.x + ENEMY_W / 2, e.y + ENEMY_H + 2, ENEMY_W / 2 + 2, 4, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // Body
      ctx.fillStyle = flash ? "#fff" : main;
      ctx.beginPath();
      ctx.roundRect(e.x + 2, e.y + 4, ENEMY_W - 4, ENEMY_H - 6, 5);
      ctx.fill();

      // Top dome
      ctx.fillStyle = flash ? "#fff" : dark;
      ctx.beginPath();
      ctx.arc(e.x + ENEMY_W / 2, e.y + 6, 9, Math.PI, 0);
      ctx.fill();

      // Eyes
      if (!flash) {
        ctx.fillStyle = light;
        ctx.fillRect(e.x + 7, e.y + 8, 4, 4);
        ctx.fillRect(e.x + ENEMY_W - 11, e.y + 8, 4, 4);
        ctx.fillStyle = "#000";
        ctx.fillRect(e.x + 8, e.y + 9, 2, 2);
        ctx.fillRect(e.x + ENEMY_W - 10, e.y + 9, 2, 2);
      }

      // HP indicator for tough enemies
      if (e.hp > 1 && !flash) {
        ctx.fillStyle = "#fff";
        ctx.font = "bold 8px monospace";
        ctx.textAlign = "center";
        ctx.fillText(`${e.hp}`, e.x + ENEMY_W / 2, e.y + 2);
        ctx.textAlign = "start";
      }

      // Tentacles with wobble
      const wobble = Math.sin(g.frame * 0.1 + e.x * 0.1) * 2;
      ctx.fillStyle = flash ? "#fff" : dark;
      ctx.fillRect(e.x + 4, e.y + ENEMY_H - 4 + wobble, 3, 5);
      ctx.fillRect(e.x + ENEMY_W / 2 - 1, e.y + ENEMY_H - 4 - wobble, 3, 5);
      ctx.fillRect(e.x + ENEMY_W - 7, e.y + ENEMY_H - 4 + wobble, 3, 5);

      if (e.hit > 0) e.hit--;
    };

    const drawPowerUp = (p: PowerUp) => {
      const colors = { shield: "#22d3ee", rapid: "#facc15", multi: "#a855f7" };
      const icons = { shield: "🛡", rapid: "⚡", multi: "✦" };
      const bobY = Math.sin(g.frame * 0.08 + p.x) * 3;
      
      ctx.fillStyle = `${colors[p.type]}40`;
      ctx.beginPath();
      ctx.arc(p.x, p.y + bobY, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = colors[p.type];
      ctx.lineWidth = 1.5;
      ctx.stroke();
      
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(icons[p.type], p.x, p.y + bobY + 4);
      ctx.textAlign = "start";
    };

    const loop = () => {
      if (ended) return;
      if (g.gameOver) return;
      g.frame++;

      // Smooth keyboard movement
      const moveSpeed = 5;
      if (keysDown.has("ArrowLeft") || keysDown.has("a")) {
        g.playerX = Math.max(0, g.playerX - moveSpeed);
      }
      if (keysDown.has("ArrowRight") || keysDown.has("d")) {
        g.playerX = Math.min(CANVAS_W - PLAYER_W, g.playerX + moveSpeed);
      }

      // Smooth touch movement - interpolate toward touch position
      if (g.touchActive) {
        const targetX = g.touchX - PLAYER_W / 2;
        const clampedTarget = Math.max(0, Math.min(CANVAS_W - PLAYER_W, targetX));
        g.playerX += (clampedTarget - g.playerX) * 0.25;
      }

      // Auto-fire (touch) or space bar
      const fireRate = g.rapidFireTimer > 0 ? 6 : 12;
      if (
        (g.touchActive || keysDown.has(" ") || keysDown.has("ArrowUp")) &&
        g.frame - g.lastShot >= fireRate
      ) {
        const cx = g.playerX + PLAYER_W / 2;
        if (g.multiShotTimer > 0) {
          g.bullets.push({ x: cx - BULLET_W / 2, y: CANVAS_H - 56 });
          g.bullets.push({ x: cx - BULLET_W / 2 - 10, y: CANVAS_H - 52 });
          g.bullets.push({ x: cx - BULLET_W / 2 + 10, y: CANVAS_H - 52 });
        } else {
          g.bullets.push({ x: cx - BULLET_W / 2, y: CANVAS_H - 56 });
        }
        g.lastShot = g.frame;
      }

      // Decrement timers
      if (g.shieldTimer > 0) g.shieldTimer--;
      if (g.rapidFireTimer > 0) g.rapidFireTimer--;
      if (g.multiShotTimer > 0) g.multiShotTimer--;
      if (g.invincibleTimer > 0) g.invincibleTimer--;
      if (g.screenShake > 0) g.screenShake--;
      if (g.comboTimer > 0) {
        g.comboTimer--;
        if (g.comboTimer <= 0) g.comboCount = 0;
      }

      // Update stars
      g.stars.forEach((s) => {
        s.y += s.speed;
        if (s.y > CANVAS_H) { s.y = 0; s.x = Math.random() * CANVAS_W; }
      });

      // Move bullets
      g.bullets = g.bullets.filter((b) => b.y > -10);
      g.bullets.forEach((b) => (b.y -= 8));

      // Move enemy bullets
      g.enemyBullets = g.enemyBullets.filter((b) => b.y < CANVAS_H + 10);
      g.enemyBullets.forEach((b) => (b.y += b.speed));

      // Move enemies
      let hitEdge = false;
      g.enemies.forEach((e) => {
        if (!e.alive) return;
        e.x += g.enemySpeed * g.enemyDir;
        if (e.x <= 2 || e.x >= CANVAS_W - ENEMY_W - 2) hitEdge = true;
      });
      if (hitEdge) {
        g.enemyDir *= -1;
        g.enemies.forEach((e) => { if (e.alive) e.y += 8; });
      }

      // Enemy shooting - varied speed
      const shootInterval = Math.max(40 - g.wave * 2, 12);
      if (g.frame % shootInterval === 0) {
        const alive = g.enemies.filter((e) => e.alive);
        if (alive.length > 0) {
          const shooter = alive[Math.floor(Math.random() * alive.length)];
          const bulletSpeed = 2.5 + Math.min(g.wave * 0.2, 2);
          g.enemyBullets.push({ x: shooter.x + ENEMY_W / 2, y: shooter.y + ENEMY_H, speed: bulletSpeed });
        }
      }

      // Move power-ups
      g.powerUps.forEach((p) => { p.y += 2.5; p.life--; });
      g.powerUps = g.powerUps.filter((p) => p.life > 0 && p.y < CANVAS_H - 10);

      // Update particles
      g.particles = g.particles.filter((p) => p.life > 0);
      g.particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.03; // slight gravity
        p.life--;
        p.size *= 0.97;
      });

      // Collision: bullets vs enemies
      g.bullets.forEach((b) => {
        g.enemies.forEach((e) => {
          if (!e.alive) return;
          if (b.x < e.x + ENEMY_W && b.x + BULLET_W > e.x &&
              b.y < e.y + ENEMY_H && b.y + BULLET_H > e.y) {
            b.y = -100;
            e.hp--;
            e.hit = 5;
            if (e.hp <= 0) {
              e.alive = false;
              g.comboCount++;
              g.comboTimer = 60;
              const comboBonus = Math.min(g.comboCount, 5);
              const points = (10 + g.wave * 2) * comboBonus;
              g.score += points;
              setScore(g.score);
              const colors = ["#ef4444", "#f59e0b", "#a855f7"];
              spawnExplosion(e.x + ENEMY_W / 2, e.y + ENEMY_H / 2, colors[e.type], 12);
              g.screenShake = 4;

              // Random power-up drop (10% chance)
              if (Math.random() < 0.1) {
                const types: PowerUp["type"][] = ["shield", "rapid", "multi"];
                g.powerUps.push({
                  x: e.x + ENEMY_W / 2,
                  y: e.y + ENEMY_H / 2,
                  type: types[Math.floor(Math.random() * types.length)],
                  life: 500,
                });
              }
            }
          }
        });
      });

      // Collision: power-ups vs player
      const px = g.playerX;
      const py = CANVAS_H - 56;
      g.powerUps.forEach((p, i) => {
        if (Math.abs(p.x - (px + PLAYER_W / 2)) < 18 && Math.abs(p.y - (py + PLAYER_H / 2)) < 18) {
          if (p.type === "shield") g.shieldTimer = 300;
          if (p.type === "rapid") g.rapidFireTimer = 300;
          if (p.type === "multi") g.multiShotTimer = 300;
          g.powerUps.splice(i, 1);
          spawnExplosion(p.x, p.y, "#22d3ee", 6);
        }
      });

      // Collision: enemy bullets vs player
      if (g.invincibleTimer <= 0) {
        g.enemyBullets.forEach((b, i) => {
          if (b.x < px + PLAYER_W && b.x + BULLET_W > px &&
              b.y < py + PLAYER_H && b.y + BULLET_H > py) {
            g.enemyBullets.splice(i, 1);
            if (g.shieldTimer > 0) {
              g.shieldTimer = 0;
              spawnExplosion(px + PLAYER_W / 2, py, "#22d3ee", 8);
            } else {
              g.playerLives--;
              g.screenShake = 8;
              g.invincibleTimer = 90;
              spawnExplosion(px + PLAYER_W / 2, py + PLAYER_H / 2, "#10b981", 12);
              if (g.playerLives <= 0) {
                g.gameOver = true;
              }
            }
          }
        });
      }

      // Enemy reached player
      g.enemies.forEach((e) => {
        if (e.alive && e.y + ENEMY_H >= py) g.gameOver = true;
      });

      // Wave cleared
      if (g.enemies.every((e) => !e.alive)) {
        g.wave++;
        setWave(g.wave);
        g.enemies = spawnEnemies(g.wave);
        g.enemySpeed = Math.min(0.3 + g.wave * 0.1, 1.5);
        g.enemyBullets = [];
        // Bonus points for wave clear
        g.score += g.wave * 50;
        setScore(g.score);
      }

      // === DRAW ===
      ctx.save();
      // Screen shake
      if (g.screenShake > 0) {
        const shakeX = (Math.random() - 0.5) * g.screenShake * 2;
        const shakeY = (Math.random() - 0.5) * g.screenShake * 2;
        ctx.translate(shakeX, shakeY);
      }

      // Background
      const bgGrad = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
      bgGrad.addColorStop(0, "#020617");
      bgGrad.addColorStop(0.3, "#0c1222");
      bgGrad.addColorStop(0.7, "#0f172a");
      bgGrad.addColorStop(1, "#020617");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(-10, -10, CANVAS_W + 20, CANVAS_H + 20);

      // Stars
      g.stars.forEach((s) => {
        ctx.fillStyle = `rgba(255,255,255,${s.brightness})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.speed > 0.35 ? 1.5 : 0.7, 0, Math.PI * 2);
        ctx.fill();
      });

      // Particles
      g.particles.forEach((p) => {
        ctx.globalAlpha = Math.min(p.life / 25, 1);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      // Power-ups
      g.powerUps.forEach((p) => drawPowerUp(p));

      // Draw player (blink during invincibility)
      if (g.invincibleTimer <= 0 || g.frame % 6 < 4) {
        drawPlayer(px, py);
      }

      // Draw enemies
      g.enemies.forEach((e) => drawEnemy(e));

      // Draw player bullets
      g.bullets.forEach((b) => {
        const bGrad = ctx.createLinearGradient(b.x, b.y, b.x, b.y + BULLET_H);
        bGrad.addColorStop(0, "#22c55e");
        bGrad.addColorStop(1, "#86efac");
        ctx.fillStyle = bGrad;
        ctx.fillRect(b.x, b.y, BULLET_W, BULLET_H);
        ctx.fillStyle = "rgba(34, 197, 94, 0.25)";
        ctx.fillRect(b.x - 2, b.y, BULLET_W + 4, BULLET_H);
      });

      // Draw enemy bullets
      g.enemyBullets.forEach((b) => {
        const glow = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, 6);
        glow.addColorStop(0, "rgba(239, 68, 68, 0.8)");
        glow.addColorStop(1, "rgba(239, 68, 68, 0)");
        ctx.fillStyle = glow;
        ctx.fillRect(b.x - 6, b.y - 6, 12, 12);
        ctx.fillStyle = "#ef4444";
        ctx.beginPath();
        ctx.arc(b.x, b.y, 3, 0, Math.PI * 2);
        ctx.fill();
      });

      // HUD
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fillRect(0, 0, CANVAS_W, 32);

      // Lives (hearts)
      for (let i = 0; i < 3; i++) {
        ctx.fillStyle = i < g.playerLives ? "#ef4444" : "#374151";
        ctx.font = "12px sans-serif";
        ctx.fillText("♥", 8 + i * 16, 22);
      }

      // Score
      ctx.fillStyle = "#10b981";
      ctx.font = "bold 13px 'Courier New', monospace";
      ctx.fillText(`⭐ ${g.score}`, 60, 22);

      // Combo indicator
      if (g.comboCount > 1 && g.comboTimer > 0) {
        ctx.fillStyle = "#facc15";
        ctx.font = "bold 11px monospace";
        ctx.fillText(`COMBO x${g.comboCount}`, 145, 22);
      }

      // Wave
      ctx.fillStyle = "#67e8f9";
      ctx.font = "bold 13px 'Courier New', monospace";
      ctx.fillText(`WAVE ${g.wave}`, CANVAS_W - 80, 22);

      // Active power-up indicators
      const indicators = [];
      if (g.shieldTimer > 0) indicators.push({ icon: "🛡", color: "#22d3ee" });
      if (g.rapidFireTimer > 0) indicators.push({ icon: "⚡", color: "#facc15" });
      if (g.multiShotTimer > 0) indicators.push({ icon: "✦", color: "#a855f7" });
      indicators.forEach((ind, i) => {
        ctx.font = "11px sans-serif";
        ctx.fillStyle = ind.color;
        ctx.fillText(ind.icon, CANVAS_W - 80 - i * 18, 22);
      });

      ctx.restore();

      // Game over handling
      if (g.gameOver) {
        ended = true;
        spawnExplosion(px + PLAYER_W / 2, py + PLAYER_H / 2, "#10b981", 16);
        setScore(g.score);
        setWave(g.wave);
        setGameState("gameover");
        submitScore(g.score);
        return;
      }

      g.animId = requestAnimationFrame(loop);
    };

    g.animId = requestAnimationFrame(loop);

    return () => {
      ended = true;
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchEnd);
      cancelAnimationFrame(g.animId);
    };
  }, [gameState]);

  if (loading) return null;
  if (!session || !player) return <Navigate to="/game" replace />;

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div className="max-w-md mx-auto px-3 py-4 space-y-3">
        {/* Lives & score header */}
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-1.5">
            <Heart className="w-4 h-4 text-red-400" />
            <span className="text-xs font-bold text-white">
              {loadingLives ? "..." : `${livesLeft}/2 daily`}
            </span>
          </div>
          <h1 className="text-base font-extrabold text-game-gradient tracking-wider">FLOW INVADERS</h1>
          <div className="flex items-center gap-1.5">
            <Trophy className="w-4 h-4 text-yellow-400" />
            <span className="text-xs font-bold text-cyan-400">{score}</span>
          </div>
        </div>

        {/* Canvas area */}
        <div className="relative flex justify-center">
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            className="rounded-xl border border-white/10 max-w-full"
            style={{
              touchAction: "none",
              width: "100%",
              maxWidth: CANVAS_W,
              background: "#020617",
            }}
          />

          {/* START overlay */}
          {gameState === "menu" && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 rounded-xl cursor-pointer p-4 text-center"
              onClick={unlocked === false ? () => navigate("/game") : startGame}
            >
              {unlocked === false ? (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mb-4 shadow-lg shadow-pink-500/40">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-xl font-extrabold text-white mb-1">LOCKED</p>
                  <p className="text-xs text-gray-300 mb-3">
                    Unlock Space Invaders for $1 (one-time) from the Dashboard.
                  </p>
                  <Button
                    onClick={(e) => { e.stopPropagation(); navigate("/game"); }}
                    className="font-bold bg-gradient-to-r from-pink-500 to-cyan-500 text-white border-0"
                    size="sm"
                  >
                    Go to Dashboard
                  </Button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center mb-4 shadow-lg shadow-purple-500/40 animate-pulse">
                    <Gamepad2 className="w-8 h-8 text-black" />
                  </div>
                  <p className="text-xl font-extrabold text-white mb-1">TAP TO START</p>
                  <p className="text-xs text-gray-400 mb-2">
                    📱 Touch to move · Auto-fire enabled
                  </p>
                  <p className="text-[10px] text-gray-500">
                    🎮 Desktop: Arrows/WASD + Space
                  </p>
                  {livesLeft !== null && livesLeft <= 0 && (
                    <div className="mt-3 text-center">
                      <p className="text-sm text-red-400 font-bold">🕐 No lives remaining!</p>
                      <p className="text-xs text-gray-400 mt-1">Come back tomorrow for 2 new lives</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* GAME OVER overlay */}
          {gameState === "gameover" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 rounded-xl">
              <p className="text-2xl font-extrabold text-white mb-1">GAME OVER</p>
              <p className="text-lg font-bold text-cyan-400 mb-1">Score: {score}</p>
              <p className="text-sm text-cyan-400">Wave: {wave}</p>
              {reward !== null && (
                <p className="text-sm text-yellow-400 font-semibold mt-2">
                  💰 +${reward.toFixed(2)} earned!
                </p>
              )}
              {submitting && (
                <p className="text-xs text-gray-500 mt-1">Saving...</p>
              )}
              {livesLeft !== null && livesLeft <= 0 ? (
                <div className="mt-4 text-center">
                  <p className="text-base font-bold text-red-400 mb-1">🕐 No lives remaining!</p>
                  <p className="text-sm text-gray-400">Come back tomorrow for 2 new lives</p>
                </div>
              ) : (
                <Button
                  onClick={startGame}
                  disabled={submitting}
                  className="mt-4 font-bold bg-gradient-to-r from-purple-500 to-cyan-500 text-black hover:from-purple-500 hover:to-cyan-300 border-0 px-8"
                  size="sm"
                >
                  Play Again
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </GameLayout>
  );
}